import { z } from "zod";
// Integration catalog contract: manifests live in @healthos/integration-configs
// (config, not code, mirroring personas); per-tenant rows live in org_integrations.
export const INTEGRATION_SLUGS = [
    "slack",
    "ms-teams",
    "outlook-calendar",
    "sso-oidc",
    "hris-rippling",
    "hris-workday",
    "claims-pbm-sftp",
    "referral-hinge-health",
];
export const IntegrationSlugSchema = z.enum(INTEGRATION_SLUGS);
export const INTEGRATION_CAPABILITIES = [
    "notification.channel",
    "identity.sso",
    "eligibility.sync",
    "measurement.claims",
    "member.referral",
    "calendar.read",
];
export const IntegrationCapabilitySchema = z.enum(INTEGRATION_CAPABILITIES);
export const INTEGRATION_STATUSES = [
    "available",
    "configuring",
    "live",
    "paused",
    "error",
];
export const IntegrationStatusSchema = z.enum(INTEGRATION_STATUSES);
export const INTEGRATION_CATEGORIES = [
    "sso",
    "messaging",
    "hris",
    "claims",
    "care-partner",
    "schedule",
];
export const INTEGRATION_AUTH_MODES = [
    "oauth2",
    "api_key",
    "sftp_pgp",
    "oidc_issuer",
    "none",
];
export const INTEGRATION_CHANNELS = ["slack", "msteams"];
export const IntegrationFieldSchema = z.object({
    key: z.string().min(1).max(80),
    label: z.string().min(1).max(120),
    kind: z.enum(["text", "url", "secret"]),
    required: z.boolean(),
    help: z.string().min(1).max(400).optional(),
});
export const IntegrationPresentationSchema = z.object({
    kindLabel: z.string().min(1).max(80),
    desc: z.string().min(1).max(400),
    scopes: z.array(z.string().min(1).max(80)).min(1).max(8),
    foot: z.object({
        available: z.string().min(1).max(240),
        live: z.string().min(1).max(240).optional(),
    }),
    brand: z.object({
        asset: z.string().min(1).max(200).optional(),
        monogram: z.string().min(1).max(4),
    }),
    action: z
        .object({
        href: z.string().min(1).max(400),
        label: z.string().min(1).max(80),
        external: z.boolean(),
    })
        .nullable()
        .optional(),
    defaultState: z.enum(["available", "soon"]),
});
/**
 * Static shape of a manifest: everything except configSchema, which is a
 * live Zod schema and is checked separately as `instanceof z.ZodType`.
 * Mirrors PersonaManifestSchema's role for persona-configs.
 */
export const IntegrationManifestStaticSchema = z.object({
    slug: IntegrationSlugSchema,
    name: z.string().min(1).max(120),
    category: z.enum(INTEGRATION_CATEGORIES),
    version: z.number().int().min(1),
    authMode: z.enum(INTEGRATION_AUTH_MODES),
    capabilities: z.array(IntegrationCapabilitySchema).min(1),
    channel: z.enum(INTEGRATION_CHANNELS).optional(),
    requiresBaa: z.boolean(),
    fields: z.array(IntegrationFieldSchema).max(20),
    presentation: IntegrationPresentationSchema,
});
/** Throws when the manifest's static shape or configSchema is invalid. */
export function validateIntegrationManifest(manifest) {
    const { configSchema, ...staticShape } = manifest;
    IntegrationManifestStaticSchema.parse(staticShape);
    if (!(configSchema instanceof z.ZodType)) {
        throw new Error(`integration manifest ${manifest.slug}: configSchema must be a Zod schema`);
    }
}
//# sourceMappingURL=integration.js.map